/* eslint-disable @typescript-eslint/no-use-before-define */
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { CustomerFormDataType } from '../components/Forms/CustomerForm/CustomerForm';
import { InventoryFormDataType } from '../components/Forms/InventoryForm/InventoryForm';
import { SupplierFormDataType } from '../components/Forms/SupplierForm/SupplierForm';
import { UserFormDataType } from '../components/Forms/UserForm/UserForm';
import apigateway from '../utils/apigateway';

/**
 *  ----------------------------------------------------------------------------
 *  ----------------------------------------------------------------------------
 * This has nothing to do with the conventional Context API.
 * It just contains helper functions for actions such as api calls.
 * If you find a better way to group this functions or methods feel free to
 * refactor and improve the code.
 *
 * Please dont misuse the FREEDOM 😉
 * ----------------------------------------------------------------------------
 * ----------------------------------------------------------------------------
 */

export function handleUserAction(
  run: (promise: Promise<unknown>) => void,
  storeId: number,
  account: UserFormDataType,
  showAlertCb: (message: string) => void,
  method: 'post' | 'put',
) {
  if (account.password !== account.confirmPassword) {
    showAlertCb('Password and Confirm password do not match!');
    return;
  }

  const fndAccount = { ...account };

  // eslint-disable-next-line no-param-reassign
  delete fndAccount.confirmPassword;

  const creatingUser = apigateway[method](`/user?store=${storeId}`, {
    ...fndAccount,
    storeId,
    ...(fndAccount.phoneNumber && {
      phoneNumber: formatPhoneNumberIntl(fndAccount.phoneNumber).replace(
        /[^0-9+]/g,
        '',
      ),
    }),
  });

  run(creatingUser);
}

export function handleCustomerAction(
  run: (promise: Promise<unknown>) => void,
  storeId: number,
  account: CustomerFormDataType,
  showAlertCb: (message: string) => void,
  method: 'post' | 'put',
) {
  if (!account.email && account.password) {
    showAlertCb(
      'You provided a password without an email. Kindly provide an email to enable login!',
    );
    return;
  }

  if (account.password !== account.confirmPassword) {
    showAlertCb('Password and Confirm password do not match!');
    return;
  }

  const fndAccount = { ...account };

  // eslint-disable-next-line no-param-reassign
  delete fndAccount.confirmPassword;

  const creatingCustomer = apigateway[method](`/customer?store=${storeId}`, {
    ...fndAccount,
    storeId,
    ...(fndAccount.phoneNumber && {
      phoneNumber: formatPhoneNumberIntl(fndAccount.phoneNumber).replace(
        /[^0-9+]/g,
        '',
      ),
    }),
  });

  run(creatingCustomer);
}

export function handleSupplierAction(
  run: (promise: Promise<unknown>) => void,
  storeId: number,
  account: SupplierFormDataType,
  showAlertCb: (message: string) => void,
  method: 'post' | 'put',
) {
  if (!account.email && account.password) {
    showAlertCb(
      'You provided a password without an email. Kindly provide an email to enable login!',
    );
    return;
  }

  if (account.password !== account.confirmPassword) {
    showAlertCb('Password and Confirm password do not match!');
    return;
  }

  const fndAccount = { ...account };

  // eslint-disable-next-line no-param-reassign
  delete fndAccount.confirmPassword;

  const creatingSupplier = apigateway[method](`/supplier?store=${storeId}`, {
    ...fndAccount,
    storeId,
    ...(fndAccount.phoneNumber && {
      phoneNumber: formatPhoneNumberIntl(fndAccount.phoneNumber).replace(
        /[^0-9+]/g,
        '',
      ),
    }),
  });

  run(creatingSupplier);
}

export function handleInventoryAction(
  run: (promise: Promise<unknown>) => void,
  storeId: number,
  product: InventoryFormDataType,
  showAlertCb: (message: string) => void,
  method: 'post' | 'put',
) {
  if (product.trackWholesale) {
    if (product.packQuantity === 0 || product.packPrice === 0) {
      showAlertCb(
        'If you want to track wholesale, provide values for "price of a pack" and "quantity in a pack"!',
      );
      return;
    }

    if (product.price > product.packPrice) {
      showAlertCb(
        'If you want to track wholesale, the price of an item (single unit) cannot be more than the pack price (Price of Carton)',
      );
      return;
    }

    if (product.packQuantity > product.quantity) {
      showAlertCb(
        'If you want to track wholesale, the quantity in a pack (carton) cannot be more than the stocks quantity (quantity)',
      );
      return;
    }
  }

  if (product.chargeTax && !product.taxes) {
    showAlertCb('If you want charge tax, kindly select a tax rate!');
    return;
  }

  const fndProduct = {
    name: product.name.trim(),
    ...(product.id && { productId: product.id }),
    storeId,
    inventory: {
      price: product.price,
      quantity: product.quantity || 0,
      status: product.status,
      inStockDate: product.inStockDate,
      stockThreshold: product.stockThreshold || 0,
      cost: product.cost || 0,
      weight: product.weight || 0,
      trackInventory: product.trackInventory,
      trackWholesale: product.trackWholesale,
      packPrice: product.trackWholesale ? product.packPrice : 0,
      packQuantity: product.trackWholesale ? product.packQuantity : 0,
      ...(product.upc && {
        upc: product.upc.replace(/\s/g, '').toLocaleUpperCase(),
      }),
      ...(product.departmentId && { departmentId: product.departmentId.value }),
    },
    ...(product.description && { description: product.description.trim() }),
    ...(product.notes && { notes: product.notes.trim() }),
    ...(product.photos && { photos: product.photos }),
    ...(product.categoryId && { categoryId: product.categoryId.value }),
    ...(product.brandId && { brandId: product.brandId.value }),
    ...(product.supplierId && { supplierId: product.supplierId.value }),
    ...(product.taxes && { taxes: product.taxes.split('|') }),
  };

  const creatingInventory = apigateway[method](
    `/product?store=${storeId}`,
    fndProduct,
  );

  run(creatingInventory);
}

// Helper functions that return validation errors or prepared data (for React Query mutations)
export function validateAndPrepareInventoryData(
  storeId: number,
  product: InventoryFormDataType,
): { error?: string; data?: any } {
  if (product.trackWholesale) {
    if (product.packQuantity === 0 || product.packPrice === 0) {
      return {
        error:
          'If you want to track wholesale, provide values for "price of a pack" and "quantity in a pack"!',
      };
    }

    if (product.price > product.packPrice) {
      return {
        error:
          'If you want to track wholesale, the price of an item (single unit) cannot be more than the pack price (Price of Carton)',
      };
    }

    if (product.packQuantity > product.quantity) {
      return {
        error:
          'If you want to track wholesale, the quantity in a pack (carton) cannot be more than the stocks quantity (quantity)',
      };
    }
  }

  if (product.chargeTax && !product.taxes) {
    return { error: 'If you want charge tax, kindly select a tax rate!' };
  }

  const data = {
    name: product.name.trim(),
    ...(product.id && { productId: product.id }),
    storeId,
    inventory: {
      price: product.price,
      quantity: product.quantity || 0,
      status: product.status,
      inStockDate: product.inStockDate,
      stockThreshold: product.stockThreshold || 0,
      cost: product.cost || 0,
      weight: product.weight || 0,
      trackInventory: product.trackInventory,
      trackWholesale: product.trackWholesale,
      packPrice: product.trackWholesale ? product.packPrice : 0,
      packQuantity: product.trackWholesale ? product.packQuantity : 0,
      ...(product.upc && {
        upc: product.upc.replace(/\s/g, '').toLocaleUpperCase(),
      }),
      ...(product.departmentId && { departmentId: product.departmentId.value }),
    },
    ...(product.description && { description: product.description.trim() }),
    ...(product.notes && { notes: product.notes.trim() }),
    ...(product.photos && { photos: product.photos }),
    ...(product.categoryId && { categoryId: product.categoryId.value }),
    ...(product.brandId && { brandId: product.brandId.value }),
    ...(product.supplierId && { supplierId: product.supplierId.value }),
    ...(product.taxes && { taxes: product.taxes.split('|') }),
  };

  return { data };
}

export function validateAndPrepareCustomerData(
  storeId: number,
  account: CustomerFormDataType,
): { error?: string; data?: any } {
  if (!account.email && account.password) {
    return {
      error:
        'You provided a password without an email. Kindly provide an email to enable login!',
    };
  }

  if (account.password !== account.confirmPassword) {
    return { error: 'Password and Confirm password do not match!' };
  }

  const { confirmPassword, ...fndAccount } = account;

  const data = {
    ...fndAccount,
    storeId,
    ...(fndAccount.phoneNumber && {
      phoneNumber: formatPhoneNumberIntl(fndAccount.phoneNumber).replace(
        /[^0-9+]/g,
        '',
      ),
    }),
  };

  return { data };
}

export function validateAndPrepareSupplierData(
  storeId: number,
  account: SupplierFormDataType,
): { error?: string; data?: any } {
  if (!account.email && account.password) {
    return {
      error:
        'You provided a password without an email. Kindly provide an email to enable login!',
    };
  }

  if (account.password !== account.confirmPassword) {
    return { error: 'Password and Confirm password do not match!' };
  }

  const { confirmPassword, ...fndAccount } = account;

  const data = {
    ...fndAccount,
    storeId,
    ...(fndAccount.phoneNumber && {
      phoneNumber: formatPhoneNumberIntl(fndAccount.phoneNumber).replace(
        /[^0-9+]/g,
        '',
      ),
    }),
  };

  return { data };
}

export function validateAndPrepareUserData(
  storeId: number,
  account: UserFormDataType,
): { error?: string; data?: any } {
  if (account.password !== account.confirmPassword) {
    return { error: 'Password and Confirm password do not match!' };
  }

  const { confirmPassword, ...fndAccount } = account;

  const data = {
    ...fndAccount,
    storeId,
    ...(fndAccount.phoneNumber && {
      phoneNumber: formatPhoneNumberIntl(fndAccount.phoneNumber).replace(
        /[^0-9+]/g,
        '',
      ),
    }),
  };

  return { data };
}
